// Simple IndexedDB image storage
// Stores images as Blobs and returns string IDs; callers can request object URLs for display

const DB_NAME = 'FTM_DB';
const STORE_NAME = 'images';
const DB_VERSION = 1;

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function generateId(): string {
  if ((window as any).crypto?.randomUUID) return (window as any).crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export const ImageService = {
  async saveImage(file: File): Promise<string> {
    const db = await openDb();
    const id = generateId();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const putReq = store.put(file, id);
      putReq.onsuccess = () => resolve();
      putReq.onerror = () => reject(putReq.error);
    });
    return id;
  },

  async saveBlob(blob: Blob): Promise<string> {
    const db = await openDb();
    const id = generateId();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const putReq = store.put(blob, id);
      putReq.onsuccess = () => resolve();
      putReq.onerror = () => reject(putReq.error);
    });
    return id;
  },

  async getImageBlob(id: string): Promise<Blob | undefined> {
    const db = await openDb();
    return await new Promise<Blob | undefined>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const getReq = store.get(id);
      getReq.onsuccess = () => resolve(getReq.result as Blob | undefined);
      getReq.onerror = () => reject(getReq.error);
    });
  },

  async getImageURL(id: string): Promise<string | undefined> {
    // Uploaded pictures are stored as their web address (2026-10-13); only old ones are local ids.
    if (/^(https?:|\/)/.test(id)) return id;
    const blob = await this.getImageBlob(id);
    if (!blob) return undefined;
    return URL.createObjectURL(blob);
  },

  async deleteImage(id: string): Promise<void> {
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const delReq = store.delete(id);
      delReq.onsuccess = () => resolve();
      delReq.onerror = () => reject(delReq.error);
    });
  },

  async resizeToJpeg(blob: Blob, maxWidth = 1600, maxHeight = 1600, quality = 0.85): Promise<Blob> {
    const img = document.createElement('img');
    const url = URL.createObjectURL(blob);
    try {
      await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = () => reject(new Error('Image load failed'));
        img.src = url;
      });
      const { width, height } = img;
      let targetW = width;
      let targetH = height;
      const ratio = Math.min(maxWidth / width, maxHeight / height, 1);
      targetW = Math.round(width * ratio);
      targetH = Math.round(height * ratio);
      const canvas = document.createElement('canvas');
      canvas.width = targetW;
      canvas.height = targetH;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Canvas not supported');
      ctx.drawImage(img, 0, 0, targetW, targetH);
      const dataUrl = canvas.toDataURL('image/jpeg', quality);
      const bin = atob(dataUrl.split(',')[1]);
      const arr = new Uint8Array(bin.length);
      for (let i = 0; i < arr.length; i++) arr[i] = bin.charCodeAt(i);
      return new Blob([arr], { type: 'image/jpeg' });
    } finally {
      URL.revokeObjectURL(url);
    }
  },
};
